/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { moesifAppKey, moesifApiBaseUrl } from 'config';
// @ts-expect-error: moesif-browser-js does not have types
import moesif from 'moesif-browser-js';
import logger from './logger';
import { isStrictlyNecessaryCookiesAllowed } from './oneTrustCookiePro';

let mo: ReturnType<typeof moesif.init> | null = null;

function getMoesifClient() {
  if (
    !mo &&
    typeof window !== 'undefined' &&
    moesifAppKey &&
    isStrictlyNecessaryCookiesAllowed()
  ) {
    mo = moesif.init({
      applicationId: moesifAppKey,
      batchEnabled: true,
      batchSize: 20,
      batchMaxTime: 5000,
      enableCrossDomainTracking: true,
      crossDomainTargets: null,
      // Only invoked for captured HTTP (XHR/fetch) events, never for
      // identify/track calls. We only want identify and track data in Moesif,
      // so drop every captured HTTP event. start() is still needed for
      // cross-domain tracking.
      // eslint-disable-next-line object-shorthand
      skip: function () {
        return true;
      },
      // eslint-disable-next-line object-shorthand
      getMetadata: function getMetadata() {
        // Not implemented
      },
    });
    mo.start();
  }
  return mo;
}

export function resetMoesif() {
  const moClient = getMoesifClient();
  if (moClient) {
    moClient.reset();
  }
}

export interface MoesifUser {
  id: string;
  email?: string;
  name?: string;
  idpId?: string;
  isWSO2User?: string;
}

export interface MoesifOrg {
  uuid: string;
  handle?: string;
  metadata?: {
    moesif_analytics_org_id?: string;
  };
}

export function identifyMoesifUser(user: MoesifUser) {
  const moClient = getMoesifClient();
  if (!moClient) return;

  const nameParts = (user.name ?? '').trim().split(/\s+/);
  moClient.identifyUser(user.id, {
    email: user.email,
    name: user.name,
    first_name: nameParts[0] || undefined,
    last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined,
    idpId: user.idpId,
    isWSO2User: user.isWSO2User,
  });
}

export function identifyMoesifCompany(org: MoesifOrg) {
  const moClient = getMoesifClient();
  if (!moClient) return;

  moClient.identifyCompany(
    org.uuid,
    {
      ...(org.handle ? { name: org.handle } : {}),
      ...(org.metadata ? { metadata: org.metadata } : {}),
    },
    org.handle
  );
}

export function publishToMoesif(name: string, properties: Record<string, any>) {
  const moClient = getMoesifClient();
  if (!moClient) return;

  moClient.track(name, properties);
}

export interface MoesifTokenClaims {
  org: string | null;
}

export function decodeMoesifTokenClaims(token: string): MoesifTokenClaims {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      org: payload?.sub ?? null,
    };
  } catch {
    return { org: null };
  }
}

export const MOESIF_TOKEN_NOT_FOUND = 'Not Found';
export const MOESIF_TOKEN_INVALID = 'Invalid Key';

// A hanging Moesif endpoint must not leave callers waiting indefinitely.
const MOESIF_ID_TOKEN_TIMEOUT_MS = 10 * 1000;

export async function fetchMoesifIdToken(
  getAccessToken: () => Promise<string>
): Promise<string | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    MOESIF_ID_TOKEN_TIMEOUT_MS
  );
  try {
    const accessToken = await getAccessToken();
    const resp = await fetch(`${moesifApiBaseUrl}/id_token`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    if (!resp.ok) {
      if (resp.status === 404) {
        return MOESIF_TOKEN_NOT_FOUND;
      }
      const errorBody = await resp.json().catch(() => ({}));
      if (
        resp.status === 500 &&
        errorBody?.message ===
          'Invalid Moesif key configured for this organization.'
      ) {
        return MOESIF_TOKEN_INVALID;
      }
      logger.error(
        `Failed to fetch Moesif token. Status: ${JSON.stringify(errorBody)}`
      );
      return null;
    }
    const data = await resp.json();
    if (!data?.token) {
      logger.error('Moesif token missing in response', data);
      return null;
    }
    return data.token;
  } catch (e) {
    logger.error('Error fetching Moesif token', e);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Resolves the Moesif organization id for the current org, or null when
 * Moesif isn't configured, is misconfigured, or the lookup fails. Never
 * rejects, so callers can fire it without awaiting.
 */
export async function fetchMoesifOrgId(
  getAccessToken: () => Promise<string>
): Promise<string | null> {
  const token = await fetchMoesifIdToken(getAccessToken);
  if (
    !token ||
    token === MOESIF_TOKEN_NOT_FOUND ||
    token === MOESIF_TOKEN_INVALID
  ) {
    return null;
  }
  return decodeMoesifTokenClaims(token).org;
}
